"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { dictionaries, Language } from './dictionaries';

import {
    enUS, de, es, fr, it, pt, el, tr, ru, ro, zhCN, ja, id, hi, ko, sr, bg, hu, pl, cs, da, sv, nb, nl
} from 'date-fns/locale';

const localeMap: Record<string, any> = {
    en: enUS, de, es, fr, it, pt, el, tr, ru, ro, zh: zhCN, ja, id, hi, ko, sr, bg, hu, pl, cs, da, sv, no: nb, nl
};

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
    dateLocale: any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
    const [language, setLanguage] = useState<Language>('en');

    // Load saved language
    useEffect(() => {
        const saved = localStorage.getItem('synthhotel-lang') as Language;
        if (saved && Object.keys(localeMap).includes(saved)) {
            setLanguage(saved);
        }
    }, []);

    // Save specific language preference
    const handleSetLanguage = (lang: Language) => {
        setLanguage(lang);
        localStorage.setItem('synthhotel-lang', lang);
        document.documentElement.lang = lang;
    };

    const t = (key: string) => {
        return dictionaries[language]?.[key] || key;
    };

    const dateLocale = localeMap[language] || enUS;

    return (
        <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t, dateLocale }}>
            {children}
        </LanguageContext.Provider>
    );
}

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
